import { ShoppingCart, User, HelpCircle, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ContactModal } from './ContactModal';
import { getCart, getCartItemCount } from '@/utils/cart';
import { getFavouriteCount } from '@/utils/favourites';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '@/contexts/SettingsContext';
import { useAuthUser } from '@/hooks/useAuthUser';
import { trackCartOpen } from '@/hooks/usePostHogTracking';
import { coverUrl } from '@/utils/coverImage';

interface HeaderProps {
  onCartClick: () => void;
}

export const Header = ({ onCartClick }: HeaderProps) => {
  const [cartCount, setCartCount] = useState(0);
  const [favCount, setFavCount] = useState(0);
  const user = useAuthUser();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { settings } = useSettings();

  const handleCartClick = () => {
    const cartData = getCart();
    trackCartOpen(null, cartData);
    onCartClick();
  };

  useEffect(() => {
    const updateCartCount = () => {
      setCartCount(getCartItemCount());
    };
    const updateFavCount = () => {
      setFavCount(getFavouriteCount());
    };

    updateCartCount();
    updateFavCount();

    window.addEventListener('storage', updateCartCount);
    window.addEventListener('cartUpdated', updateCartCount);
    window.addEventListener('favouritesUpdated', updateFavCount);

    return () => {
      window.removeEventListener('storage', updateCartCount);
      window.removeEventListener('cartUpdated', updateCartCount);
      window.removeEventListener('favouritesUpdated', updateFavCount);
    };
  }, []);

  return (
    <header className="sticky top-0 z-50 w-full glass">
      <div className="container mx-auto px-2 md:px-4 py-2 md:py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 cursor-pointer"
              type="button"
            >
              <img 
                src={coverUrl(settings.logo_url, 'logo') || settings.logo_url || "/lovable-uploads/61dab0b5-7f0c-46f5-9cb9-feeb93237e94.png"} 
                alt={settings.site_name || "Equiphoto.ch"} 
                className="h-6 md:h-8 lg:h-10 no-drag no-select"
                onContextMenu={(e) => e.preventDefault()}
                draggable={false}
              />
            </button>
          </div>

          {/* Right side controls */}
          <div className="flex items-center space-x-4">
            <LanguageSwitcher />
            
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsContactModalOpen(true)}
              className="relative rounded-full"
              title={t('contact.contactUs')}
            >
              <HelpCircle className="w-5 h-5" />
              <span className="sr-only">{t('contact.contactUs')}</span>
            </Button>
            
            <Link to="/favourites">
              <Button
                variant="ghost"
                size="icon"
                className="relative rounded-full"
                title={t('favourites.title', 'Preferiti')}
              >
                <Heart className={`w-5 h-5 transition-colors ${favCount > 0 ? 'fill-red-500 text-red-500' : ''}`} />
                {favCount > 0 && (
                  <Badge 
                    className="absolute -top-1 sm:-top-2 -right-1 sm:-right-2 h-5 w-5 flex items-center justify-center p-0 bg-red-500 text-white text-xs font-bold"
                  >
                    {favCount > 99 ? '99+' : favCount}
                  </Badge>
                )}
                <span className="sr-only">{t('favourites.title', 'Preferiti')}</span>
              </Button>
            </Link>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCartClick}
              className="relative"
            >
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <Badge 
                  className="absolute -top-1 sm:-top-2 -right-1 sm:-right-2 h-5 w-5 flex items-center justify-center p-0 bg-primary text-primary-foreground text-xs font-bold"
                >
                  {cartCount}
                </Badge>
              )}
              <span className="sr-only">{t('navigation.cart')}</span>
            </Button>

            <Link to={user ? "/account" : "/auth"}>
              <Button
                variant="ghost"
                size="sm"
              >
                <User className="w-5 h-5" />
                <span className="hidden sm:inline ml-2">
                  {user ? t('navigation.account') : t('navigation.login')}
                </span>
                <span className="sr-only">{user ? t('navigation.account') : t('navigation.login')}</span>
              </Button>
            </Link>
          </div>
        </div>
      </div>
      
      <ContactModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)} 
      />
    </header>
  );
};
